import Log from "../common/Log";
import axios from "axios";
import {
    BUILD_TYPE_DEVELOPMENT,
    BUILD_TYPE_DEVELOPMENT_LOCAL,
    BUILD_TYPE_PRODUCTION,
    DEVELOPMENT_BACK_URL,
    LOCAL_URL_BACKEND,
    PRODUCTION_BACK_URL,
} from "../context/ApplicationContextProvider";
import {RESPONSE_INTERRUPTED} from "../context/ModelContextProvider";

// all functions throwable
class BaseModel {
    domain = "";
    actions = null;

    constructor(actions) {
        this.setActions(actions);
        if (process.env.REACT_APP_BUILD_CONFIG === BUILD_TYPE_DEVELOPMENT_LOCAL) {
            this.domain = `${LOCAL_URL_BACKEND}`;
        } else if (process.env.REACT_APP_BUILD_CONFIG === BUILD_TYPE_DEVELOPMENT) {
            this.domain = DEVELOPMENT_BACK_URL;
        } else if (process.env.REACT_APP_BUILD_CONFIG === BUILD_TYPE_PRODUCTION) {
            this.domain = PRODUCTION_BACK_URL;
        }
    }

    setActions(actions) {
        this.actions = actions;
    }

    onBuildKeyFormat(parent_key, key) {
        return `${parent_key}.${key}`;
    }

    /**
     * @param {
     *      path: {string},
     *      headers: {object|null},
     *      data: {object|null},
     * } request
     * @returns promise: {object|null}
     */
    get(request) {
        return this._request({
            method: "get",
            ...request,
            params: request.params,
            data: null,
        });
    }

    /**
     * @param {
     *      path: {string},
     *      headers: {object|null},
     *      data: {object|null},
     * } request
     * @returns promise: {object|null}
     */
    post(request) {
        return this._request({
            method: "post",
            ...request,
            params: null,
            data: request.params,
        });
    }

    /**
     * @param {
     *      path: {string},
     *      headers: {object|null},
     *      data: {object|null},
     * } request
     * @returns promise: {object|null}
     */
    delete(request) {
        return this._request({
            method: "delete",
            ...request,
            params: null,
            data: request.params,
        });
    }

    /**
     * @param {
     *      path: {string},
     *      headers: {object|null},
     *      data: {object|null},
     * } request
     * @returns promise: {object|null}
     */
    put(request) {
        return this._request({
            method: "put",
            ...request,
            params: null,
            data: request.params,
        });
    }

    /**
     * @param {
     *      path: {string},
     *      domain: {string},
     *      method: {"get|post|put|delete"},
     *      path: {string},
     *      headers: {object|null},
     *      data: {object|null},
     *      params: {object|null},
     *      with_cache: {bool|default true}
     * } request
     * @returns promise: {object|null}
     */

    custom(request) {
        return this._request(request);
    }

    // 커리어월릿 포맷 --> {code, message keyword, data}
    // @see cache_policy: ModelContextProvider.set(request_obj, response_obj, cache_policy)
    async _request({
        domain,
        method,
        path,
        headers,
        data,
        params,
        with_cache = false,
        cache_policy = null,
        handle_authorization = true,
    }) {
        let authorization = this.actions.getToken() || "";
        let default_headers = {
            "Content-Type": "application/x-www-form-urlencoded",
            Accept: "application/json",
        };
        if (authorization) {
            default_headers["Authorization"] = `BEARU ${authorization}`;
        }
        let merged_header = {
            ...default_headers,
            ...headers,
        };

        try {
            let request_obj = {
                url: `${domain || this.domain}/${path}`,
                method: method,
                headers: merged_header,
                data: this._getData(merged_header["Content-Type"], data),
                params: params,
            };

            let axios_response = null; // nullable (cause: cached value)
            let axios_response_data = null; // notnull (서버 리스폰스 등에 대한 익셉션이 아닌경우)

            if (with_cache && this.actions.enable_cache) {
                let saved = this.actions.cache.get(request_obj);
                if (saved) {
                    axios_response_data = saved;
                } else {
                    axios_response = await axios(request_obj);
                    axios_response_data = axios_response.data;
                    let is_checked = this.actions.cache.check(axios_response_data);
                    if (is_checked) {
                        this.actions.cache.set(request_obj, axios_response_data, cache_policy);
                    }
                }
            } else {
                // @TODO cache bug fix
                // Log.private("cache > skip");
                axios_response = await axios(request_obj);
                axios_response_data = axios_response.data;
            }

            // 401 만 가로채서 로그인을 유도한다.
            if (handle_authorization && axios_response_data?.code === 401) {
                await this.actions.onRequireLogin();
                return RESPONSE_INTERRUPTED;
            } else {
                return axios_response_data;
            }
        } catch (__e) {
            let res = null;
            try {
                res = __e.response.data;
                if (handle_authorization && res.code === 401) {
                    await this.actions.onRequireLogin();
                    res = RESPONSE_INTERRUPTED;
                }
            } catch {}
            Log.error("model/error", __e, path, res);
            return res;
        }
    }

    _getData(content_type, data) {
        if (content_type === "multipart/form-data") {
            return this._objectToFormData(data);
        } else if (content_type === "application/x-www-form-urlencoded") {
            let form_data = this._objectToFormData(data);
            return form_data != null ? new URLSearchParams(form_data).toString() : null;
        } else if (content_type === "application/json") {
            return data;
        } else {
            // @TODO not implemented yet another content_type
            return data;
        }
    }

    _buildFormData(form_data, data, parent_key) {
        if (
            data &&
            typeof data === "object" &&
            !(data instanceof Date) &&
            !(data instanceof File) &&
            !(data instanceof Blob)
        ) {
            Object.keys(data).forEach((key) => {
                this._buildFormData(form_data, data[key], parent_key ? this.onBuildKeyFormat(parent_key, key) : key);
            });
        } else {
            const value = data === null ? "" : data;
            form_data.append(parent_key, value);
        }
    }

    _objectToFormData(data) {
        if (!data) return null;
        const formData = new FormData();
        this._buildFormData(formData, data);
        return formData;
    }
}

export default BaseModel;
